/* eslint-disable import/no-named-as-default-member */
/* eslint-disable camelcase */
/* eslint-disable jsx-a11y/anchor-has-content */

// node modules
import React, { Component } from 'react';
import { Field, propTypes, reduxForm, submit, SubmissionError } from 'redux-form';
import { connect } from 'react-redux';
import withStyles from 'isomorphic-style-loader/withStyles';
import PropTypes from 'prop-types';
import { sprintf } from 'sprintf-js';
import * as EmailValidator from 'email-validator';
import classNames from 'classnames';
import passwordSheriff from 'password-sheriff';
import Tooltip from 'rc-tooltip';

// app modules: actions
import { signupUserByEmail, socialSignin, cleardown } from 'actions/auth';
import { register, socialLogin } from 'actions/nativeAuth';

// app: components
import SocialLoginButtons from 'components/SocialLoginButtons';
import SpinnerButton from 'components/SpinnerButton';
import { CheckboxField, InputField } from 'components/Field';
import OfferPrice from 'components/OfferPrice';
import SubscriptionSteps from 'components/SubscriptionSteps';

// app: services
import analytics from 'services/analytics';

// assets
import CssModalDialogContent from 'components/ModalDialog/ModalDialogContent.scss';

const correctCommonEmailDomains = (email, corrections = {}) => {
  const [localPart, domain] = email.split('@');
  if (domain && corrections[domain]) {
    return `${localPart}@${corrections[domain]}`;
  }
  return email;
};

class SignupForm extends Component {
  static propTypes = {
    signupMessage: PropTypes.string,
    showPrice: PropTypes.bool,
    signupUserByEmail: PropTypes.func.isRequired,
    socialSignin: PropTypes.func.isRequired,
    error: PropTypes.string,
    socialAuthInProgress: PropTypes.string,
    showSteps: PropTypes.bool,
    returnTo: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    emailDomainCorrections: PropTypes.objectOf(PropTypes.string),
    ...propTypes,
  };

  static defaultProps = {
    error: null,
    signupMessage: null,
    showPrice: false,
    socialAuthInProgress: null,
    showSteps: false,
    returnTo: null,
    emailDomainCorrections: {},
  };

  constructor(props) {
    super(props);

    this.state = {
      passwordValidationErrors: null,
      displayPasswordHints: false,
      showPassword: false,
    };

    this.bound = {
      onSubmit: this.onSubmit.bind(this),
      onSocialButtonClick: this.onSocialButtonClick.bind(this),
      onPasswordChange: this.onPasswordChange.bind(this),
      onPasswordBlur: this.onPasswordBlur.bind(this),
      onPasswordFocus: this.onPasswordFocus.bind(this),
      renderPasswordError: this.renderPasswordError.bind(this),
      normalizeEmail: this.normalizeEmail.bind(this),
      togglePasswordVisibility: this.togglePasswordVisibility.bind(this),
    };
  }

  componentWillUnmount() {
    this.props.cleardown();
  }

  static renderPasswordErrorItems(items, ruleKey) {
    if (items) {
      const ruleItems = items.map((item, itemIndex) => {
        const ruleItemKey = `${ruleKey}-${itemIndex}`;
        const ruleItemClasses = classNames(
          'modal-dialog-content__rule',
          { 'modal-dialog-content__rule--ok': item.verified },
          { 'modal-dialog-content__rule--nok': !item.verified },
        );
        return (
          <li key={ruleItemKey} className={ruleItemClasses}>
            {item.message}
          </li>
        );
      });
      return <ul>{ruleItems}</ul>;
    }
    return null;
  }

  normalizeEmail(value) {
    return correctCommonEmailDomains(value || '', this.props.emailDomainCorrections);
  }

  async onSocialButtonClick(provider) {
    const { authParams, platform, locationState, returnTo } = this.props;
    analytics.sendEvent({
      event: 'Auth.Register',
      method: provider,
    });
    if (platform === 'app') {
      this.props.socialLogin(provider, locationState ? locationState.returnTo : null);
    } else {
      this.props.socialSignin({ provider, authParams, returnTo });
    }
  }

  async onSubmit(values) {
    const { authParams, platform, locationState, returnTo } = this.props;
    const { email, password, given_name, family_name, email_opt_in } = values;

    const name = `${given_name} ${family_name}`;
    const metadata = {
      name,
      given_name,
      family_name,
      email_opt_in: email_opt_in ? 'true' : 'false',
    };
    analytics.sendEvent({
      event: 'Auth.Register',
      method: 'email',
    });
    try {
      if (platform === 'app') {
        await this.props.register({
          email,
          password,
          metadata,
          returnTo: locationState ? locationState.returnTo : null,
        });
      } else {
        await this.props.signupUserByEmail({ email, password, metadata, authParams, returnTo });
      }
    } catch (error) {
      const feedback = {};
      if (error.name === 'PasswordNoUserInfoError') {
        feedback.password = 'Must not contain personal information';
      } else if (error.code === 'user_exists') {
        feedback.email = 'A user already exists with this email';
      } else {
        feedback._error = error.description || error.message;
      }
      throw new SubmissionError(feedback);
    }
  }

  onPasswordChange(evt) {
    const errors = this.props.validate({ password: evt.target.value });
    this.setState({ passwordValidationErrors: errors._password });
  }

  onPasswordFocus() {
    this.setState({ displayPasswordHints: true });
  }

  onPasswordBlur() {
    this.setState({ displayPasswordHints: false });
  }

  renderPasswordError() {
    const { passwordValidationErrors } = this.state;
    if (passwordValidationErrors && passwordValidationErrors.rules) {
      const ruleItems = passwordValidationErrors.rules.map((rule, ruleIndex) => {
        const ruleKey = `password-error_${ruleIndex}`;
        const ruleClasses = classNames(
          'modal-dialog-content__rule',
          { 'modal-dialog-content__rule--ok': rule.verified },
          { 'modal-dialog-content__rule modal-dialog-content__rule--nok': !rule.verified },
        );
        return (
          <li key={ruleKey} className={ruleClasses}>
            {sprintf(rule.message, rule.format)}
            {SignupForm.renderPasswordErrorItems(rule.items, ruleKey)}
          </li>
        );
      });
      return <ul className="modal-dialog-content__tooltip">{ruleItems}</ul>;
    }
    return null;
  }

  togglePasswordVisibility() {
    this.setState(prevState => ({ showPassword: !prevState.showPassword }));
  }

  render() {
    const {
      handleSubmit,
      loading,
      signupMessage,
      showSteps,
      signupError,
      displayType,
      showPrice,
      socialAuthInProgress,
    } = this.props;
    const { passwordValidationErrors, displayPasswordHints, showPassword } = this.state;
    return (
      <div className="modal-dialog-content" data-cy="signup-form">
        {showPrice ? <OfferPrice className="modal-dialog-content__centered" /> : null}
        {showSteps ? <SubscriptionSteps /> : null}
        {signupMessage && <p className="modal-dialog-content__centered">{signupMessage}</p>}
        <form onSubmit={handleSubmit(this.bound.onSubmit)}>
          <Field label="First name" name="given_name" type="text" component={InputField} />
          <Field label="Last name" name="family_name" type="text" component={InputField} />
          <Field
            label="Email Address"
            name="email"
            type="email"
            component={InputField}
            normalize={this.bound.normalizeEmail}
          />
          <Tooltip
            trigger={['focus']}
            defaultVisible={false}
            visible={
              !!passwordValidationErrors &&
              !passwordValidationErrors.verified &&
              displayPasswordHints
            }
            overlay={this.bound.renderPasswordError}
            placement={displayType === 'desktop' ? 'right' : 'bottom'}
            overlayStyle={{ zIndex: 1410 }}
            overlayClassName="rc-tooltip--light"
            id="PasswordValidationRules"
            align={displayType === 'desktop' ? { offset: [0, -8] } : { offset: [0, -24] }}
          >
            <Field
              label="Password"
              name="password"
              type="password"
              component={InputField}
              onChange={this.bound.onPasswordChange}
              onFocus={this.bound.onPasswordFocus}
              onBlur={this.bound.onPasswordBlur}
              togglePasswordVisibility={this.bound.togglePasswordVisibility}
              showPassword={showPassword}
            />
          </Tooltip>
          <Field
            label="Receive ckbk newsletter and recipe tips?"
            name="email_opt_in"
            component={CheckboxField}
          />
          {typeof signupError === 'string' && (
            <p className="modal-dialog-content__error">{signupError}</p>
          )}
          <SpinnerButton
            type="submit"
            text="Sign up"
            loading={loading}
            onClick={handleSubmit(this.bound.onSubmit)}
          />
          <p className="margin-top-5">
            <a
              href="https://www.ckbk.com/terms"
              className="link link--energized"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </a>{' '}
            /{' '}
            <a
              href="https://www.ckbk.com/privacy"
              className="link link--energized"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
          </p>
        </form>
        <div className="modal-dialog-content__hr-wrap">
          <div className="modal-dialog-content__hr" />
          <p className="modal-dialog-content__hr__text">or sign up with</p>
          <div className="modal-dialog-content__hr" />
        </div>
        <SocialLoginButtons
          inProgress={socialAuthInProgress}
          onSocialButtonClick={this.bound.onSocialButtonClick}
        />
      </div>
    );
  }
}

const passwordPolicy = passwordSheriff('fair');
function validate(values) {
  const errors = {};
  if (!values.given_name) {
    errors.given_name = 'Required';
  }
  if (values.given_name?.includes('@')) {
    errors.given_name = 'Must be a name – not an email address';
  }
  if (values.family_name?.includes('@')) {
    errors.family_name = 'Must be a name – not an email address';
  }

  if (!values.email) {
    errors.email = 'Required';
  } else if (!EmailValidator.validate(values.email)) {
    errors.email = 'Must be a valid email address';
  }

  if (!values.password) {
    errors.password = 'Required';
  } else if (!passwordPolicy.check(values.password)) {
    errors.password = 'Invalid Password';
    errors._password = passwordPolicy.missing(values.password);
  }
  return errors;
}

function mapStateToProps(state, ownProps) {
  const { error, code, timestamp, forgotMsg, loading } = state.auth;
  return {
    signupError: error,
    signupErrorCode: code,
    timestamp,
    forgotMsg,
    loading,
    authParams: { ...state.config.auth, ...ownProps.authParams },
    signupMessage: ownProps.signupMessage || state.paywall.signupMessage,
    showSteps: ownProps.showSteps || state.paywall.showSteps,
    showPrice: ownProps.showPrice || state.paywall.showPriceOnSignup,
    initialValues: { email_opt_in: true },
    displayType: state.dimensions.displayType,
    platform: state.config.platform,
    locationState: state.router.locationState,
    socialAuthInProgress: state.auth.socialAuthInProgress,
    emailDomainCorrections: state.config.emailDomainCorrections || {},
  }; // To default to true for non-EU: state.user.geolocation.continent !== 'EU',
}

const mapDispatchToProps = {
  signupUserByEmail,
  socialSignin,
  submit,
  cleardown,
  register,
  socialLogin,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(reduxForm({ validate, form: 'SignupForm' })(withStyles(CssModalDialogContent)(SignupForm)));
